import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const sanitize = (value) => (typeof value === "string" ? value.trim() : "");

const sanitizeImage = (image) => {
    if (!image) return null;
    // Handle both direct URL string and object with url property
    const url = typeof image === "string"
        ? sanitize(image)
        : sanitize(image?.url || "");
    if (!url) return null;
    return {
        url,
        alt: typeof image === "string" ? "" : sanitize(image?.alt || "")
    };
};

// Helper to check if URL is internal
const isInternalUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed.startsWith("/")) return true;
    if (trimmed.startsWith("mailto:") || trimmed.startsWith("tel:")) return false;
    try {
        const urlObj = new URL(trimmed, window.location.origin);
        return urlObj.origin === window.location.origin;
    } catch {
        return false;
    }
};

const InfoSection = ({ fields }) => {
    const navigate = useNavigate();

    const sanitizedData = useMemo(() => {
        if (!fields) return null;

        const boxes = Array.isArray(fields.boxes)
            ? fields.boxes
                .map((box, index) => {
                    if (!box) return null;

                    // Extract icon - handle various structures
                    let icon = null;
                    if (box?.icon !== null && box?.icon !== undefined && box?.icon !== false) {
                        // If icon is a string URL
                        if (typeof box.icon === "string") {
                            const url = box.icon.trim();
                            if (url) {
                                icon = { url, alt: "" };
                            }
                        }
                        // If icon is an object
                        else if (box.icon && typeof box.icon === "object") {
                            // Direct URL access (most common case)
                            const iconUrl = box.icon.url;
                            if (iconUrl && typeof iconUrl === "string" && iconUrl.trim()) {
                                icon = {
                                    url: iconUrl.trim(),
                                    alt: (box.icon.alt || box.icon.title || "").toString().trim()
                                };
                            }
                            // If direct access failed, try sanitizeImage
                            if (!icon) {
                                icon = sanitizeImage(box.icon);
                            }
                        }
                    }

                    const title = sanitize(box?.title || "");
                    const description = sanitize(box?.description || "");

                    // Allow box to render if it has at least one field
                    if (!icon && !title && !description) return null;

                    return {
                        icon,
                        title,
                        description,
                    };
                })
                .filter(Boolean)
            : [];

        const buttonText = sanitize(fields.button_text || "");
        const buttonUrl = sanitize(fields.button_url || "");

        const hasContent = boxes.length > 0 || (buttonText && buttonUrl);
        if (!hasContent) return null;

        return {
            boxes,
            buttonText,
            buttonUrl,
        };
    }, [fields]);

    if (!sanitizedData) return null;

    const { boxes, buttonText, buttonUrl } = sanitizedData;

    return (
        <div className="canvas">
            <section className="info-section">
                {boxes.length > 0 && (
                    <div className="info-grid">
                        {boxes.map((box, index) => {
                            const { icon, title, description } = box;
                            if (!icon && !title && !description) return null;

                            // Ensure icon URL exists before rendering
                            const hasIcon = icon && icon.url && typeof icon.url === "string" && icon.url.trim();

                            return (
                                <div key={`info-box-${index}`} className="info-box" data-stagger-index={index}>
                                    {hasIcon && (
                                        <img src={icon.url} alt={icon.alt || title || ""} />
                                    )}
                                    {(title || description) && (
                                        <div className="info-box-sub">
                                            {title && <h2>{title}</h2>}
                                            {description && (
                                                <p dangerouslySetInnerHTML={{ __html: description.replace(/\r\n/g, "<br />") }} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Button */}
                {buttonText && buttonUrl && (
                    <div className="btn-box" data-stagger-index={boxes.length}>
                        {(() => {
                            const isInternal = isInternalUrl(buttonUrl);
                            return isInternal ? (
                                <button
                                    className="special-btn"
                                    onClick={() => navigate(buttonUrl)}
                                >
                                    {buttonText}{" "}
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M5 15L15 5M15 5H7.5M15 5V12.5"
                                            stroke="white"
                                            strokeWidth="1.6"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            ) : (
                                <a
                                    href={buttonUrl}
                                    className="special-btn"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {buttonText}{" "}
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M5 15L15 5M15 5H7.5M15 5V12.5"
                                            stroke="white"
                                            strokeWidth="1.6"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </a>
                            );
                        })()}
                    </div>
                )}
            </section>
        </div>
    );
};

export default InfoSection;

